import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { brandPayment, formatMoney, formatMonthlyAmount } from 'src/services/brandPaymentService';
import { CommonHelper } from 'src/utils/commonHelper';
import { LocalStorage } from 'src/utils/storage/local-storage';
import { SessionStorage } from 'src/utils/storage/session-storage';

export default function BrandPaymentPage() {
  const { t } = useTranslation();
  const { brandId } = useParams();
  const [status, setStatus] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const loggedIn = !!LocalStorage.getItem('token');
  const brands = CommonHelper.GetBrandInfoFromLocalStorage('brand_details') || [];
  const canPay = CommonHelper.GetBrandBillingAccess(brandId).canPay;
  const load = useCallback(async () => {
    const current = await brandPayment.status(brandId);
    setStatus(current);
    if (current.payment_enabled) setInvoices(await brandPayment.invoices(brandId));
  }, [brandId]);
  useEffect(() => {
    if (!loggedIn || !canPay) return undefined;
    let active = true;
    setStatus(null);
    setError('');
    brandPayment
      .status(brandId)
      .then((data) => {
        if (active) setStatus(data);
      })
      .catch((e) => {
        if (active) setError(e.message);
      });
    brandPayment
      .invoices(brandId)
      .then((data) => {
        if (active) setInvoices(data || []);
      })
      .catch(() => {});
    let attempts = 0;
    const timer = setInterval(() => {
      attempts += 1;
      if (attempts >= 12) clearInterval(timer);
      brandPayment
        .status(brandId)
        .then((data) => {
          if (active) setStatus(data);
        })
        .catch(() => {});
    }, 5000);
    return () => {
      active = false;
      clearInterval(timer);
    };
  }, [brandId, loggedIn, canPay]);
  const perform = async (action, redirect = false) => {
    setBusy(true);
    setError('');
    try {
      const data = await action();
      if (redirect) window.location.assign(data.url);
      else await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  const enterBrand = (id) => {
    SessionStorage.setItem('current_brand_id', JSON.stringify(Number(id)));
    window.location.assign(CommonHelper.DefaultPermissionRoute || '/');
  };
  if (loggedIn && !canPay)
    return (
      <Box p={4}>
        <Alert severity="info">
          {t('billing_payout.payment_is_unavailable_for_this_user_only_brand_administrators_of_payment_enabled_res')}
        </Alert>
        <Button href="/">{t('billing_payout.back_to_application')}</Button>
      </Box>
    );
  return (
    <Box sx={{ maxWidth: 720, mx: 'auto', p: 3, width: '100%' }}>
      <Card>
        <CardContent>
          <Stack spacing={2}>
            <Typography variant="h4">{status?.brand_name || t('billing_payout.brand_subscription')}</Typography>
            {!loggedIn ? (
              <>
                <Alert severity="info">
                  {t(
                    'billing_payout.sign_in_to_view_your_brand_subscription_your_checkout_payment_is_confirmed_separately'
                  )}
                </Alert>
                <Button href="/login">{t('billing_payout.sign_in')}</Button>
              </>
            ) : (
              <>
                {error && <Alert severity="error">{error}</Alert>}
                {!status && !error && <CircularProgress />}
                {status && (
                  <>
                    <Typography>
                      {status.payment_enabled
                        ? t('billing_payout.per_month', { amount: formatMonthlyAmount(status.amount, status.currency) })
                        : t('billing_payout.payment_is_not_required_for_this_brand')}
                    </Typography>
                    <Alert severity={status.access_allowed ? 'success' : 'info'}>
                      {status.access_allowed
                        ? t('billing_payout.you_can_access_this_brand')
                        : t('billing_payout.a_paid_subscription_is_required_to_access_this_brand')}
                    </Alert>
                    {status.subscription_status && (
                      <Typography>
                        {t('billing_payout.subscription', {
                          status: t(`billing_payout.status_${status.subscription_status}`, {
                            defaultValue: status.subscription_status.replaceAll('_', ' '),
                          }),
                        })}
                      </Typography>
                    )}
                    {status.paid_through && (
                      <Typography>
                        {t('billing_payout.paid_through', { date: new Date(status.paid_through).toLocaleString() })}
                      </Typography>
                    )}
                    {status.cancellation_pending && (
                      <Alert severity="warning">
                        {t('billing_payout.cancellation_is_pending_confirmation_we_will_retry_automatically')}
                      </Alert>
                    )}
                    {status.cancel_at_period_end && (
                      <Alert severity="info">
                        {t('billing_payout.cancellation_scheduled', {
                          date: new Date(status.expire_date).toLocaleDateString(),
                        })}
                      </Alert>
                    )}
                    {status.access_allowed && (
                      <Button variant="contained" onClick={() => enterBrand(brandId)}>
                        {t('billing_payout.open_brand')}
                      </Button>
                    )}
                    {status.payment_enabled && !status.can_pay && (
                      <Alert severity="info">
                        {t('billing_payout.contact_your_brand_administrator_to_manage_payment')}
                      </Alert>
                    )}
                    {status.payment_enabled && status.can_pay && status.payment_action === 'subscribe' && (
                      <Button
                        variant="contained"
                        disabled={busy}
                        onClick={() => perform(() => brandPayment.link(brandId), true)}
                      >
                        {t('billing_payout.subscribe_monthly')}
                      </Button>
                    )}
                    {status.payment_enabled && status.can_pay && status.payment_action === 'manage' && (
                      <>
                        <Button
                          variant="contained"
                          disabled={busy}
                          onClick={() => perform(() => brandPayment.portal(brandId), true)}
                        >
                          {t('billing_payout.manage_subscription_pay_invoice')}
                        </Button>
                        <Button
                          disabled={busy}
                          onClick={() =>
                            perform(() =>
                              status.cancel_at_period_end ? brandPayment.keep(brandId) : brandPayment.cancel(brandId)
                            )
                          }
                        >
                          {status.cancel_at_period_end
                            ? t('billing_payout.keep_subscription')
                            : t('billing_payout.cancel_at_end_of_paid_period')}
                        </Button>
                      </>
                    )}
                    <Button
                      disabled={busy}
                      onClick={() =>
                        perform(() => (status.can_pay ? brandPayment.refresh(brandId) : brandPayment.status(brandId)))
                      }
                    >
                      {t('billing_payout.refresh_payment_status')}
                    </Button>
                  </>
                )}
                {brands.length > 1 && (
                  <TextField
                    select
                    label={t('billing_payout.switch_brand')}
                    value=""
                    onChange={(e) => enterBrand(e.target.value)}
                  >
                    {brands
                      .filter((b) => b.is_enabled)
                      .map((b) => (
                        <MenuItem key={b.brand_id} value={b.brand_id}>
                          {b.brand_name}
                        </MenuItem>
                      ))}
                  </TextField>
                )}
                <Button onClick={CommonHelper.Logout}>{t('billing_payout.logout')}</Button>
              </>
            )}
          </Stack>
        </CardContent>
      </Card>
      {invoices.length > 0 && (
        <Box mt={3}>
          <Typography variant="h6">{t('billing_payout.payment_history')}</Typography>
          {invoices.map((invoice) => (
            <Card key={invoice.id} sx={{ my: 1 }}>
              <CardContent>
                <Typography>
                  {formatMoney(invoice.amount, invoice.currency)} -{' '}
                  {t(`billing_payout.status_${invoice.status}`, { defaultValue: invoice.status })}
                </Typography>
                <Typography variant="body2">
                  {invoice.paid_at
                    ? new Date(invoice.paid_at).toLocaleDateString()
                    : t('billing_payout.awaiting_payment')}
                </Typography>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}
    </Box>
  );
}

export function BrandCheckoutPage() {
  const { t } = useTranslation();
  const { token } = useParams();
  const [offer, setOffer] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    let active = true;
    brandPayment
      .offer(token)
      .then((data) => {
        if (active) setOffer(data);
      })
      .catch(() => {
        if (active)
          setError(
            t('billing_payout.this_payment_link_is_expired_or_unavailable_request_a_new_link_from_your_reseller')
          );
      });
    return () => {
      active = false;
    };
  }, [token, t]);
  const pay = async () => {
    setBusy(true);
    setError('');
    try {
      const data = await brandPayment.start(token, offer.brand_id);
      window.location.assign(data.url);
    } catch (e) {
      setError(e.message);
      setBusy(false);
    }
  };
  return (
    <Box sx={{ maxWidth: 520, mx: 'auto', p: 4 }}>
      <Card>
        <CardContent>
          <Stack spacing={3}>
            <Typography variant="h4">{offer?.brand_name || t('billing_payout.monthly_subscription')}</Typography>
            {error && <Alert severity="error">{error}</Alert>}
            {!offer && !error && <CircularProgress />}
            {offer && (
              <>
                <Typography variant="h5">
                  {t('billing_payout.per_month', { amount: formatMoney(offer.amount, offer.currency) })}
                </Typography>
                <Typography>
                  {t(
                    'billing_payout.you_will_be_charged_monthly_until_you_cancel_complete_your_subscription_on_the_secure'
                  )}
                </Typography>
                {!CommonHelper.GetBrandBillingAccess(offer.brand_id).canPay && (
                  <Alert severity="info">
                    {t('billing_payout.sign_in_as_this_brand_s_administrator_to_make_payment')}
                  </Alert>
                )}
                <Button
                  variant="contained"
                  disabled={busy || !CommonHelper.GetBrandBillingAccess(offer.brand_id).canPay}
                  onClick={pay}
                >
                  {busy ? t('billing_payout.opening_checkout') : t('billing_payout.continue_to_payment')}
                </Button>
              </>
            )}
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}
